import { Inject, Injectable } from '@angular/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { TextModel } from '../../models/primitive/text.model';
import { ConfigService } from '../config/config.service';
import { MaterialModel } from '../../models/low-level/material.model';
import { MATERIAL_STATUS_CODE } from '../../models/low-level/material-status.model';
import { CONSUME_CODE } from '../../models/low-level/material.enum';
import { MaterialCheckModel } from '../../models/low-level/material-check.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { isDefined } from '../../utilities/is-defined';

@Injectable({
    providedIn: 'root',
})
export class MaterialUtilitiesService {
    public constructor(
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly configService: ConfigService,
    ) {}

    public getInitialSelectionForRadio(options: Array<DisplayDecorator<TextModel>>): number {
        // other countries select n/a by default
        return options.findIndex((option) => option.model.name === ConfigConstants.returnNotSelected);
    }

    public getInvalidSelectionForRadio(options: Array<DisplayDecorator<TextModel>>): number {
        const notSelectedIndex = options.findIndex((option) => option.model.name === ConfigConstants.returnNotSelected);
        if (notSelectedIndex === -1) {
            // NGMAA - there are no invalid selection for AA
            // so if there is no n/a then do not invalidate other options
            return options.length + 1;
        }
        return notSelectedIndex;
    }

    public isSpecialDangerousGoodsReturn(material: MaterialModel): boolean {
        return (
            this.configService.getSpecialDangerousGoodsInd(material.dangerousGoodsIndicator) &&
            material.isBatteryRemoved.selectedEntry.value !== ConfigConstants.optionValueYes
        );
    }

    public getOldSerialNumberList(
        material: MaterialModel,
        notification: NotificationModel,
    ): Array<DisplayDecorator<TextModel>> {
        return notification.equipmentSet
            .filter(
                (equipment) =>
                    equipment.sernr.value.length > 0 &&
                    equipment.matnr.value === material.materialIdentifier.materialNumber.value,
            )
            .map((equipment) => {
                const tid = equipment.tidnr.value.length > 0 ? equipment.tidnr.value : '--';
                return new DisplayDecorator<TextModel>(
                    `${equipment.sernr.value} - TID (${tid})`,
                    new TextModel('SerialNumber', equipment.sernr.value),
                );
            });
    }

    public clone(material: MaterialModel, primaryMaterial: MaterialModel): MaterialModel {
        // update all the required fields for the new model
        material.materialIdentifier = primaryMaterial.materialIdentifier;
        material.description.value = primaryMaterial.description.value;
        material.deliveredQuantity = primaryMaterial.deliveredQuantity;
        material.tubeIndicator = primaryMaterial.tubeIndicator;
        material.serialPIndicator = primaryMaterial.serialPIndicator;
        material.dangerousGoodsIndicator = primaryMaterial.dangerousGoodsIndicator;
        material.showDangerousInfo = primaryMaterial.showDangerousInfo;
        material.batchNumber.value = primaryMaterial.batchNumber.value;
        material.awbDelivery = primaryMaterial.awbDelivery;
        material.ucrNumber = primaryMaterial.ucrNumber;
        material.unNumber = primaryMaterial.unNumber;
        material.dangerousGoodsText = primaryMaterial.dangerousGoodsText;
        material.hazardousGoodsText = primaryMaterial.hazardousGoodsText;
        material.quantity.value = primaryMaterial.quantity.value;
        material.maxQuantity = primaryMaterial.quantity.value ?? 0;
        material.billing = primaryMaterial.billing;
        material.billingChangeable = primaryMaterial.billingChangeable;
        material.isBootStockScrapable = primaryMaterial.isBootStockScrapable;
        material.isBootstockMaterial = primaryMaterial.isBootstockMaterial;
        material.quantity.options = {
            ...material.quantity.options,
            maxValue: primaryMaterial.maxQuantity,
        };
        material.quantityUnit = primaryMaterial.quantityUnit;
        material.materialStatus.statusCode = primaryMaterial.materialStatus.statusCode;
        material.statusString.value = primaryMaterial.statusString.value;
        material.lsCrChangeable = primaryMaterial.lsCrChangeable;
        material.localScrapReturnConsumed = primaryMaterial.localScrapReturnConsumed;
        material.localScrapReturnWithoutConsumption = primaryMaterial.localScrapReturnWithoutConsumption;
        material.amount.value = primaryMaterial.amount.value;
        material.repairPrice = primaryMaterial.repairPrice;
        material.currency = primaryMaterial.currency;
        material.customerPrice = primaryMaterial.customerPrice;
        material.customerCurrency = primaryMaterial.customerCurrency;
        material.textLine = primaryMaterial.textLine;
        material.csmlTrackerUrl = primaryMaterial.csmlTrackerUrl;
        material.isRepairable = primaryMaterial.isRepairable;
        material.isQualityPart = primaryMaterial.isQualityPart;
        material.spqDocId = primaryMaterial.spqDocId;
        material.isSpqSubmitted = primaryMaterial.isSpqSubmitted;
        material.isRentalTool = primaryMaterial.isRentalTool;
        material.isPurchaseOrderTaxiBillable = primaryMaterial.isPurchaseOrderTaxiBillable;
        material.batchNumberLegacy = primaryMaterial.batchNumberLegacy;
        if (isDefined(material.serialNumberOld) && isDefined(primaryMaterial.serialNumberOld)) {
            material.serialNumberOld.value = primaryMaterial.serialNumberOld?.value;
            material.serialNumberDropdown = undefined;
        }
        if (isDefined(primaryMaterial.serialNumberDropdown)) {
            material.setSerialNumberOldField();
            material.serialNumberDropdown?.deselect();
            material.serialNumberOld = undefined;
        }
        material.serialNumberNew.value = primaryMaterial.serialNumberNew.value;
        if (primaryMaterial.billableCode.selectedEntry) {
            material.billableCode.selectByValue(primaryMaterial.billableCode.selectedEntry.value);
        }
        material.billableCode.options = {
            ...material.billableCode.options,
            disabled: primaryMaterial.billableCode.options.disabled,
        };

        if (
            material.materialStatus.statusCode === MATERIAL_STATUS_CODE.PICKED ||
            material.materialStatus.statusCode === MATERIAL_STATUS_CODE.NOTRECEIVED
        ) {
            // activity date needs to be set only for Picked and NotReceived materials
            material.activityDate.value = primaryMaterial.activityDate.value;
        }
        material.setRentalToolOptions();
        return material;
    }

    public loadSerialNumber(consumeCode: string, material: MaterialModel): void {
        if (consumeCode === CONSUME_CODE.CONSUMED) {
            if (!material.isRentalTool) {
                material.returnReason.deselect();
            }
            if (isDefined(material.serialNumberOld)) {
                material.serialNumberOld.value = material.serialNumberOld.value || material.originalSerialNumberOld;
            }
            material.serialNumberNew.value = material.serialNumberNew.value || material.originalSerialNumberNew;
        }
    }

    public getMaterialsWithGlobalStorageLocation(response: MaterialCheckModel[]): MaterialCheckModel[] {
        const globalStorageLocation = this.configService.getGlobalStorageLocationMatRequest();
        if (globalStorageLocation.length > 0) {
            const allMaterialsWithGlobalStorageLoc = response.filter(
                (part) => part.storageLoc.value === globalStorageLocation,
            );
            if (allMaterialsWithGlobalStorageLoc.length !== response.length) {
                response.forEach((part) => {
                    const isPartIncluded = allMaterialsWithGlobalStorageLoc.some(
                        (matWithPO) => matWithPO.materialNr.value === part.materialNr.value,
                    );
                    if (!isPartIncluded) {
                        part.storageLoc.value = globalStorageLocation;
                        allMaterialsWithGlobalStorageLoc.push(part);
                    }
                });
            }
            return allMaterialsWithGlobalStorageLoc;
        }
        return response;
    }

    public getSparePartCatalogMatLink(mainEquipment: string): string {
        const sparePartCatalogMatLink = this.configService.getSparePartCatalogMatLink();

        if (sparePartCatalogMatLink) {
            return `${sparePartCatalogMatLink}${mainEquipment}`;
        }
        return '';
    }
}
